import { Box, Typography } from "@mui/material";
import { ReportFilters } from "../../../../domain/models/report-filters";
import { CustomerDatabaseStore } from "../../../../store/main/CustomerDatabaseStore";
import { useEffect, useState } from "react";
import ReportFiltersComponent from "../ReportFiltersComponent";
import ReportScreen from "../../../../common/enums/ReportScreens";
import ReportFiltersLoading from "../ReportFiltersLoading";
import moment from "moment";
import DeviationPerformance from "./sub-component/DeviationPerformance";
import DeviationTable from "./sub-component/DeviationTable";
import DeviationPerformanceChart from "./sub-component/DeviationPerformanceChart";
import { TourDeviationReportStore } from "../../../../store/main/TourDeviationReportStore";
import { TerritoryDataStore } from "../../../../store/main/TerritoryDataStore";
import { TerritoryData } from "../../../../domain/models/territory-data";
import RolesId from "../../../../common/enums/RoleIds";

export type UserTourCardUser = {
  full_name: string;
  territory: string;
  min_deviation_threshold: number;
  max_deviation_threshold: number;
};

export type UserTourCardSummary = {
  month: string;
  year: string;
  total_users: number;
  working_days: number;
  total_deviations: number;
  approved_deviations: number;
  pending_deviations: number;
  deviation_percentage: number;
};

export type UserTourCardResponse = {
  user: UserTourCardUser;
  summary: UserTourCardSummary[];
  success: boolean;
};

export type DeviationUser = {
  user_id: number;
  user_name: string;
  territory_name: string | null;
  deviation_count: number;
  working_days: number;
  deviation_percentage: number;
};

export type userTourCardDataResponse = {
  users: DeviationUser[];
  success: boolean;
};

const TourDeviationReportDesktop = () => {
  const { userDetails, decodeToken, userRole } = CustomerDatabaseStore();
  const { addTourDeviation, addUserTourDeviation } = TourDeviationReportStore();
  const {
    zoneMasterData,
    regionMasterData,
    areaMasterData,
    headquarterMasterData,
  } = TerritoryDataStore();

  const currentDate = new Date();
  const defaultMonth = currentDate.getMonth() + 1;
  const defaultYear = currentDate.getFullYear();

  const [month, setMonth] = useState<number>(defaultMonth);
  const [year, setYear] = useState<number>(defaultYear);
  const [selectedZoneGACode, setSelectedZoneGACode] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [tourCardData, setTourCardData] = useState<UserTourCardResponse | null>(
    null
  );
  const [userTourCardData, setUserTourCardData] =
    useState<userTourCardDataResponse | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<
    "Self" | "ZBM" | "RBM" | "ABM" | "TSO"
  >("Self");
  const [resetFiltersTrigger, setResetFiltersTrigger] = useState<number>(0);

  const roleMapping: Record<
    "Self" | "ZBM" | "RBM" | "ABM" | "TSO" | "CM",
    RolesId | 0
  > = {
    Self: 0,
    TSO: RolesId.HEADQUATER_MANAGER,
    ABM: RolesId.AREA_MANAGER,
    RBM: RolesId.REGION_MANAGER,
    ZBM: RolesId.ZONE_MANAGER,
    CM: RolesId.COUTNRY_MANAGER,
  };

  // Decode token on mount
  useEffect(() => {
    decodeToken();
    const initialPayload = {
      month: defaultMonth.toString().padStart(2, "0"),
      year: defaultYear,
      scope: "self",
      ga_code: "",
    };
    addTourDeviationData(initialPayload);

    const userInitialPayload = {
      month: defaultMonth.toString().padStart(2, "0"),
      year: defaultYear,
      ga_code: "",
      scope: "self",
    };
    addUserTourDeviationData(userInitialPayload);
  }, []);

  // API function
  const addTourDeviationData = async (payload: {
    month: string;
    year: number;
    ga_code: string;
  }) => {
    setLoading(true);
    try {
      const responseData = await addTourDeviation(payload);
      // ⬅️ Store the API response in state
      if (responseData && responseData.success) {
        setTourCardData(responseData);
      }
    } catch (error) {
      console.error("Error fetching tour deviation data:", error);
    } finally {
      setLoading(false);
    }
  };

  const addUserTourDeviationData = async (payload: {
    month: string;
    year: number;
    ga_code: string;
    scope?: string;
  }) => {
    setLoading(true);
    try {
      const responseData = await addUserTourDeviation(payload);
      if (responseData && responseData.success) {
        setUserTourCardData({ ...responseData });
      }
    } catch (error) {
      console.error("Error fetching user tour deviation data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter data via UI
  const filterData = (filter: ReportFilters) => {
    let selectedGaCode: TerritoryData | undefined;

    // ✅ Pick latest selected GA code priority: Headquarter → Area → Region → Zone
    if (filter.headquarter?.length > 0) {
      selectedGaCode = headquarterMasterData.find(
        (item) => item.id.toString() === filter.headquarter[0]
      );
    } else if (filter.area?.length > 0) {
      selectedGaCode = areaMasterData.find(
        (item) => item.id.toString() === filter.area[0]
      );
    } else if (filter.region?.length > 0) {
      selectedGaCode = regionMasterData.find(
        (item) => item.id.toString() === filter.region[0]
      );
    } else if (filter.zone?.length > 0) {
      selectedGaCode = zoneMasterData.find(
        (item) => item.id.toString() === filter.zone[0]
      );
    }
    setSelectedZoneGACode(selectedGaCode ? selectedGaCode.ga_code : "");

    let selectedLevel: "Self" | "ZBM" | "RBM" | "ABM" | "TSO" = "Self";

    if (filter.headquarter?.length > 0) {
      selectedLevel = "TSO";
    } else if (filter.area?.length > 0) {
      selectedLevel = "ABM";
    } else if (filter.region?.length > 0) {
      selectedLevel = "RBM";
    } else if (filter.zone?.length > 0) {
      selectedLevel = "ZBM";
    }

    setSelectedLevel(selectedLevel);
    const role_id = roleMapping[selectedLevel];

    const isGeoSelected =
      filter.headquarter?.length > 0 ||
      filter.area?.length > 0 ||
      filter.region?.length > 0 ||
      filter.zone?.length > 0;

    // ✅ Extract month and year
    const monthValue = parseInt(moment(filter.date?.toString()).format("MM"));
    const yearValue = parseInt(moment(filter.date?.toString()).format("YYYY"));

    setMonth(monthValue);
    setYear(yearValue);

    // ✅ Create payload with latest GA code
    const filterPayload = {
      month: monthValue.toString().padStart(2, "0"),
      year: yearValue,
      ga_code: selectedGaCode ? selectedGaCode.ga_code : "",
      ...(role_id !== 0 && { role_id }),
      ...(!isGeoSelected && { scope: "self" }),
    };

    const userFilterPayload = {
      month: monthValue.toString().padStart(2, "0"),
      year: yearValue,
      ga_code: selectedGaCode ? selectedGaCode.ga_code : "",
      ...(role_id !== 0 && { role_id }),
      ...(!isGeoSelected && { scope: "self" }),
    };
    // ✅ Trigger API call
    addTourDeviationData(filterPayload);
    addUserTourDeviationData(userFilterPayload);
  };

  const clearData = async () => {
    setMonth(defaultMonth);
    setYear(defaultYear);
    setSelectedLevel("Self");
    setSelectedZoneGACode("");
    // trigger UI reset signal
    setResetFiltersTrigger((prev) => prev + 1);

    const payload = {
      month: defaultMonth.toString().padStart(2, "0"),
      year: defaultYear,
      scope: "self",
      ga_code: "",
    };

    setLoading(true);
    try {
      await Promise.all([
        addTourDeviationData(payload),
        addUserTourDeviationData(payload),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleUserTabSwitch = (payload: any) => {
    // Explicit clear action from child
    if (payload?.action === "clear") {
      clearData();
      return;
    }

    // Backwards compatible: if child still sends scope:'self'
    if (payload?.scope === "self") {
      clearData();
      return;
    }
    addUserTourDeviationData(payload);
  };

  const handleTourTabSwitch = (payload: any) => {
    if (payload?.action === "clear") {
      clearData();
      return;
    }

    if (payload?.scope === "self") {
      clearData();
      return;
    }

    addTourDeviationData(payload);
  };

  return (
    <>
      <Typography variant="h5" fontWeight={550} mb={1}>
        Tour Deviation Report
      </Typography>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box flexGrow={1} mr={2}>
          {Object.keys(userDetails).length > 0 ? (
            <ReportFiltersComponent
              key={resetFiltersTrigger}
              type={ReportScreen.TOUR_DEVIATION_REPORT}
              successCallback={filterData}
              clearSuccessCallBack={clearData}
              userRole={userRole.role}
              userDetails={userDetails}
              gridColumns={20}
              territoryMultiSelect={false}
            />
          ) : (
            <ReportFiltersLoading />
          )}
        </Box>
      </Box>

      {/* Performance Cards */}
      <Box
        display="flex"
        gap={2}
        mb={3}
        sx={{
          flexWrap: "nowrap",
          "@media (max-width: 550px)": {
            flexWrap: "wrap",
          },
        }}
      >
        <Box sx={{ width: { xs: "100%", sm: "360px" }}}>
          <DeviationPerformance
            month={month}
            year={year}
            ga_code={selectedZoneGACode}
            tourCardData={tourCardData}
          />
        </Box>

        <Box
          sx={{ width: { xs: "100%", sm: "400px" }, maxWidth: "400px" }}
        >
          <DeviationPerformanceChart
            month={month}
            year={year}
            ga_code={selectedZoneGACode}
            tourCardData={tourCardData}
          />
        </Box>
      </Box>

      <DeviationTable
        key={`${month}-${year}-${selectedZoneGACode}-${selectedLevel}`}
        month={month}
        year={year}
        ga_code={selectedZoneGACode}
        userTourCardData={{ ...userTourCardData }}
        userRoleId={userDetails?.role_id}
        autoSelectedTab={selectedLevel}
        onTabSwitchTour={(payload) => handleTourTabSwitch(payload)}
        onTabSwitchUser={(payload) => handleUserTabSwitch(payload)}
      />
    </>
  );
};

export default TourDeviationReportDesktop;
